import { Fragment, memo, useMemo, useState } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { Grid, useEventCallback } from '@mui/material';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import {
  DataWizardAct,
  LastStepVM,
  ParentVM,
  RecapVM,
  SectionVM,
  SignatureActVM,
  TextActVM,
} from 'civil-status/view-models/step-form';
import { useFormikContext } from 'formik';
import { getValueList } from 'civil-status/constants/states-act';
import { SubjectsWizardUseCase } from 'shell/models/subjects-wizard';
import { isDisabledValid } from 'civil-status/utils/is-disabled-valid';
import { formStepWithErrors } from 'civil-status/utils/form-step-with-errors';
import { KeySections, KeySubjects } from 'civil-status/constants/keys-subjects-section';
import OtpButton from 'civil-status/components/shared/otp-button';
import DeleteDialog from 'shell/components/shared/delete-dialog';
import ModalAdvancedForm, {
  AdvancedFormVM,
} from 'civil-status/components/step/modals/modal-advanced-edit';
import { ID_NUMBERS_COMPARENTI } from 'civil-status/components/step/form/last-step/accordions/section/list/list-number';

interface TitleStepProps {
  onGetDataSaved: (isSetRecap: boolean) => void;
  getTextAct?: () => void;
  onConfirmRegister?: () => void;
  title?: string;
  oldTitle?: string;
  isLastStep?: boolean;
  isLastStepEditAct?: boolean;
  isView?: boolean;
  idAnsc?: string;
  idEvento?: number;
  dateFormationAct?: string;
  lastRecap?: RecapVM;
  soggettiUseCaseData?: Array<SubjectsWizardUseCase>;
  disabled?: boolean;
  isSignatureAct?: boolean;
  isSignatureUSC?: boolean;
  isCertificateConformity?: boolean;
  isCommunications?: boolean;
  withoutAdvancedEditing?: boolean;
  isLastStepEmergency?: boolean;
  isCompleteSignature?: boolean;
  isWithButtonEmergency?: boolean;
  isStepFromEmergency?: boolean;
  isWithButtonNotifications?: boolean;
}

const TitleStepComponent = ({
  onGetDataSaved,
  getTextAct,
  onConfirmRegister,
  title = '',
  oldTitle = '',
  idAnsc,
  idEvento,
  dateFormationAct,
  lastRecap,
  soggettiUseCaseData = [],
  isLastStep = false,
  isLastStepEditAct = false,
  disabled = false,
  isSignatureAct = false,
  isSignatureUSC = false,
  isCertificateConformity = false,
  isView = false,
  isCommunications = false,
  withoutAdvancedEditing = false,
  isLastStepEmergency = false,
  isWithButtonEmergency = false,
  isStepFromEmergency = false,
  isCompleteSignature = false,
  isWithButtonNotifications = false,
}: TitleStepProps): React.ReactElement => {
  const formContext = useFormikContext<LastStepVM>();

  const values: LastStepVM = useMemo(() => {
    return formContext?.values || {};
  }, [formContext?.values]);

  const [openModalEdit, setOpenModalEdit] = useState<boolean>(false);
  const [openModalRegister, setOpenModalRegister] = useState<boolean>(false);

  const saveDataAdvanced = useHttpClient(`${__ACT_API_URL__}/bozza/modifica-avanzata`, {
    options: {
      method: 'PUT',
      loading: true,
      errorCatching: true,
    },
  });

  // values sezione Dati morte
  const valuesDatiDecesso: SectionVM | undefined = useMemo(() => {
    return values[KeySections.keyDatiDecesso];
  }, [values[KeySections.keyDatiDecesso]]);

  // nella sezione scelto 1 comparente
  const isWith1Comparente: boolean = useMemo(() => {
    return valuesDatiDecesso?.numberAppearing === ID_NUMBERS_COMPARENTI.ID_COMPARENTE1;
  }, [valuesDatiDecesso?.numberAppearing]);

  // nella sezione scelti 2 comparenti
  const isWith2Comparente: boolean = useMemo(() => {
    return valuesDatiDecesso?.numberAppearing === ID_NUMBERS_COMPARENTI.ID_COMPARENTE2;
  }, [valuesDatiDecesso?.numberAppearing]);

  // values soggetto Deceduto
  const valuesDeceduto: ParentVM | undefined = useMemo(() => {
    return values[KeySubjects.keyDeceduto];
  }, [values]);

  // values sezione Dati generali di riconoscimentoList
  const valuesDatiGeneraliRiconoscimento: SectionVM | undefined = useMemo(() => {
    return values[KeySections.keyDatiGeneraliRiconoscimento];
  }, [values[KeySections.keyDatiGeneraliRiconoscimento]]);

  const withErrors: boolean = useMemo(() => {
    return formStepWithErrors(
      formContext?.errors,
      isWith1Comparente,
      isWith2Comparente,
      valuesDeceduto,
      valuesDatiGeneraliRiconoscimento,
      soggettiUseCaseData
    );
  }, [
    formContext?.errors,
    valuesDatiGeneraliRiconoscimento,
    isWith1Comparente,
    isWith2Comparente,
    valuesDeceduto,
    soggettiUseCaseData,
  ]);

  const disabledValid = useMemo(() => {
    const keysValues = Object.keys(values);
    let keyWithTwins = '';

    keysValues.forEach((keyItem) => {
      const dataSubject = values[keyItem] as ParentVM;

      if (dataSubject?.generality?.isWithTwins && dataSubject?.generality?.currentTwin === 1) {
        keyWithTwins = keyItem;
      }
    });

    return isDisabledValid(isLastStep, withErrors, values as LastStepVM, keyWithTwins);
  }, [formContext?.values, isLastStep, withErrors]);

  const onSaveModalAdvanced = useEventCallback((valuesAll: AdvancedFormVM) => {
    const date = valuesAll.date?.toFormat('yyyy-MM-dd') || '';
    const hours = valuesAll.time?.toFormat('THHmmssSSS') || '';
    const dataFormazioneAtto =
      DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd THHmmssSSS') || undefined;

    const data = {
      dataFormazioneAtto,
      serie1Atto: valuesAll.series1 || undefined,
      serie2Atto: valuesAll.series2 || undefined,
      parteAtto: valuesAll.part || undefined,
      ruoloOperatore: valuesAll.role?.toString() || undefined,
      key: { idEvento },
    };

    (
      saveDataAdvanced({
        data,
      }) as unknown as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      // se abilitato effettuare chiamata per generare il testo dell'atto

      if (
        !disabledValid &&
        isLastStep &&
        !(values.textAct as TextActVM)?.isEditText &&
        getTextAct
      ) {
        // se abilitato effettuare chiamata per generare il testo dell'atto

        (
          getTextAct() as unknown as ResponsePromise<
            HttpResponse<DataWizardAct>,
            HttpResponse<DataWizardAct>
          >
        ).then((response) => {
          const castedTextAct: string =
            (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.evento
              ?.composizioneCompleta || '';

          formContext?.setFieldValue('textAct', {
            ...(values.textAct || {}),
            text: castedTextAct,
          } as TextActVM);
        });
      }

      setOpenModalEdit(false);
      // ricaricare i dati una volta salvati
      onGetDataSaved(true);
    });
  });

  const onClickConfirmRegister = useEventCallback(() => {
    setOpenModalRegister(false);

    if (onConfirmRegister) {
      onConfirmRegister();
    }
  });

  const formContextTitle = useFormikContext<SignatureActVM>();

  const titleStateAct: string = useMemo(() => {
    if (isSignatureUSC) {
      return "Firma dell'Ufficiale di Stato Civile per registrare l'atto:";
    }
    if (isCertificateConformity) {
      return "Attestazione di conformità degli allegati per l'atto:";
    }
    if (isSignatureAct) {
      return "Acquisizione delle firme per l'atto:";
    }
    if (isCommunications) {
      return 'Stampa comunicazioni atto:';
    }
    return 'Consultazione atto:';
  }, [
    isSignatureUSC,
    isCertificateConformity,
    isSignatureAct,
    isCommunications,
    isView,
    isCompleteSignature,
  ]);

  return (
    <Fragment>
      <Grid item xs={12} display="flex" justifyContent="space-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          {isSignatureAct ||
          isSignatureUSC ||
          isView ||
          isCertificateConformity ||
          isCompleteSignature ? (
            <>
              {titleStateAct}
              &nbsp;
              <span style={{ fontWeight: 'bold' }}>
                {formContextTitle.values?.idAnsc || idAnsc || ''}
              </span>
            </>
          ) : (
            `${isLastStepEditAct ? 'Rettifica: ' : ''}${title}`
          )}
        </Typography>
        {isSignatureAct ? (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignContent: 'center',
            }}
          >
            Stato: &nbsp;
            <span style={{ fontWeight: 'bold' }}>
              {getValueList(formContextTitle.values?.idState?.toString())?.toUpperCase()}
            </span>
          </span>
        ) : undefined}
        <OtpButton isWithButtonNotifications={isWithButtonNotifications} />
        {isWithButtonEmergency ? (
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => setOpenModalRegister(true)}
            disabled={!isLastStep || disabled}
            style={{ marginRight: '1rem' }}
          >
            {isLastStepEmergency ? 'DISATTIVA REG. DI EMERGENZA' : 'REGISTRO DI EMERGENZA'}
          </PmiButton>
        ) : undefined}
        {isSignatureAct ||
        isSignatureUSC ||
        isView ||
        isCertificateConformity ||
        withoutAdvancedEditing ? undefined : (
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => setOpenModalEdit(true)}
            disabled={!isLastStep || disabled || isStepFromEmergency}
          >
            MODIFICA AVANZATA
          </PmiButton>
        )}
        {openModalEdit ? (
          <ModalAdvancedForm
            onClose={() => {
              setOpenModalEdit(false);
            }}
            open={openModalEdit}
            onSave={onSaveModalAdvanced}
            dateFormationAct={dateFormationAct}
            lastRecap={lastRecap}
          />
        ) : undefined}
        {openModalRegister ? (
          <DeleteDialog
            isOpen={openModalRegister}
            onAbort={() => setOpenModalRegister(false)}
            onClose={() => setOpenModalRegister(false)}
            onConfirm={onClickConfirmRegister}
            labelOk="Conferma"
            text={
              isLastStepEmergency
                ? 'Sei sicuro di voler disattivare la modalità registro di emergenza?'
                : 'Sei sicuro di voler passare alla modalità registro di emergenza?'
            }
          />
        ) : null}
      </Grid>
      {oldTitle ? (
        <Grid item xs={12}>
          <Typography
            color="secondary.main"
            style={{
              fontSize: '26px',
            }}
          >
            {oldTitle}
          </Typography>
        </Grid>
      ) : undefined}
      {isSignatureAct || isSignatureUSC ? (
        <Grid item xs={12}>
          Ufficiale di stato civile: &nbsp;
          <span style={{ fontWeight: 'bold' }}>
            {formContextTitle.values?.operatorName || ''} &nbsp;
            {formContextTitle.values?.operatorSurname || ''}
          </span>
        </Grid>
      ) : undefined}
      {isLastStepEmergency ? (
        <Grid item xs={12}>
          <span style={{ color: 'red', fontWeight: 'bold' }}>REGISTRO DI EMERGENZA ATTIVO</span>
        </Grid>
      ) : undefined}
    </Fragment>
  );
};

TitleStepComponent.displayName = 'Title Step Component';

const TitleStep = memo(TitleStepComponent);

export default TitleStep;
